// src/components/layout/GoogleReviewRenderer.jsx
// Composant partagé : rendu d'une page "Avis Google".
// Utilisé par GoogleReviewPageEditor (aperçu) et GoogleReviewPagePublic (page réelle).

import { QRCodeSVG } from 'qrcode.react'
import { Star } from 'lucide-react'
import { FONTS, getTheme, getBgStyle, getButtonStyle } from '../../lib/themeConfig'
import { normalizeSettings } from '../../lib/normalizeSettings'
import { getReviewDesign, DEFAULT_TAP_TEXT } from '../../lib/googleReviewDesigns'

const MONO = "ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace"
const GOLD = '#FBBF24'

/* ============================================
   Helpers
   ============================================ */
function readableText(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim())
  if (!m) return '#ffffff'
  const n = parseInt(m[1], 16)
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? '#111827' : '#ffffff'
}

function Stars({ value, size = 20, color = GOLD, stroke, glow = false }) {
  const n = Math.max(0, Math.min(5, Math.round(parseFloat(value) || 0)))
  return (
    <div className="flex" style={glow ? { filter: `drop-shadow(0 0 6px ${color})` } : undefined}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          style={{ width: size, height: size }}
          fill={i < n ? color : 'none'}
          stroke={stroke || color}
          strokeWidth={1.5}
        />
      ))}
    </div>
  )
}

// Lien cliquable sur la page publique, simple bloc dans l'aperçu de l'éditeur
function Tap({ href, preview, className, style, children }) {
  if (preview) return <div className={className} style={style}>{children}</div>
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className} style={style}>
      {children}
    </a>
  )
}

function NfcIcon({ size = 48, color = '#111' }) {
  return (
    <svg
      width={size}
      height={size * 1.15}
      viewBox="0 0 48 56"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="12" y="2" width="24" height="44" rx="4" />
      <line x1="20" y1="6" x2="28" y2="6" />
      <path d="M17 19a10 10 0 0 1 14 0" />
      <path d="M20.5 22.5a5.5 5.5 0 0 1 7 0" />
      <circle cx="24" cy="26" r="0.9" fill={color} />
      <text x="24" y="39" textAnchor="middle" fontSize="8" fontWeight="800" fill={color} stroke="none" fontFamily="Arial, sans-serif">
        NFC
      </text>
    </svg>
  )
}

function QR({ value, size, fg = '#000000', bg = '#ffffff' }) {
  return <QRCodeSVG value={value} size={size} fgColor={fg} bgColor={bg} level="M" />
}

// Cadre à 4 coins autour du QR code (comme sur les cartes NFC)
function CornerFrame({ children, color = '#111827', pad = 14, radius = 16, len = 24, thickness = 3 }) {
  const base = { position: 'absolute', width: len, height: len, borderColor: color, borderStyle: 'solid', borderWidth: 0 }
  return (
    <div style={{ position: 'relative', padding: pad }}>
      <span style={{ ...base, top: 0, left: 0, borderTopWidth: thickness, borderLeftWidth: thickness, borderTopLeftRadius: radius }} />
      <span style={{ ...base, top: 0, right: 0, borderTopWidth: thickness, borderRightWidth: thickness, borderTopRightRadius: radius }} />
      <span style={{ ...base, bottom: 0, left: 0, borderBottomWidth: thickness, borderLeftWidth: thickness, borderBottomLeftRadius: radius }} />
      <span style={{ ...base, bottom: 0, right: 0, borderBottomWidth: thickness, borderRightWidth: thickness, borderBottomRightRadius: radius }} />
      {children}
    </div>
  )
}

function Avatar({ src, size, radius = '9999px', className = '' }) {
  if (!src) return null
  return (
    <img
      src={src}
      alt=""
      className={`object-cover shrink-0 ${className}`}
      style={{ width: size, height: size, borderRadius: radius }}
    />
  )
}

/* ============================================
   0. SIMPLE (modèle d'origine)
   ============================================ */
function SimpleDesign({ d }) {
  return (
    <div className="w-full max-w-xs flex flex-col items-center">
      {d.logo && <Avatar src={d.logo} size={d.preview ? 80 : 96} radius="1rem" className="mb-4" />}
      <h1 className={`${d.preview ? 'text-xl' : 'text-2xl'} font-bold mb-4`}>{d.title}</h1>

      {d.rating && (
        <div className="flex items-center gap-1.5 mb-7">
          <Stars value={d.rating} size={d.preview ? 20 : 24} />
          <span className="text-sm opacity-80">
            {d.rating}{d.reviewCount ? ` · ${d.reviewCount} avis` : ''}
          </span>
        </div>
      )}

      <Tap
        href={d.googleUrl}
        preview={d.preview}
        className="block w-full py-3.5 rounded-xl text-sm font-semibold transition-transform hover:scale-[1.02]"
        style={d.buttonStyle}
      >
        {d.ctaLabel}
      </Tap>
    </div>
  )
}

/* ============================================
   1. CARTE NFC (carte blanche, QR encadré, 5 étoiles)
   ============================================ */
function CardDesign({ d }) {
  const w = d.preview ? 250 : 300
  const k = w / 300

  return (
    <Tap href={d.googleUrl} preview={d.preview} className="block" style={{ width: w }}>
      <div
        className="bg-white text-slate-900 flex flex-col items-center text-center"
        style={{
          borderRadius: 22 * k,
          padding: `${26 * k}px ${20 * k}px ${18 * k}px`,
          aspectRatio: '54 / 85.6',
          boxShadow: '0 24px 48px -12px rgba(0,0,0,.35)',
        }}
      >
        <NfcIcon size={40 * k} color="#111827" />

        {d.showQr && (
          <div style={{ marginTop: 12 * k }}>
            <CornerFrame color={d.accent} pad={14 * k} radius={16 * k} len={24 * k} thickness={3}>
              <QR value={d.qrValue} size={Math.round(150 * k)} />
            </CornerFrame>
          </div>
        )}

        <div style={{ marginTop: 14 * k }}>
          <Stars value="5" size={26 * k} />
        </div>

        <p style={{ fontSize: 14 * k, fontWeight: 600, marginTop: 10 * k, lineHeight: 1.25 }}>
          {d.tapText}
        </p>
        <div style={{ fontSize: 34 * k, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.2, marginTop: 2 * k }}>
          Google
        </div>

        <div className="mt-auto flex items-center gap-2 text-slate-500" style={{ fontSize: 11 * k }}>
          <Avatar src={d.logo} size={20 * k} />
          <span className="truncate" style={{ maxWidth: 200 * k }}>{d.title}</span>
        </div>
      </div>
    </Tap>
  )
}

/* ============================================
   2. NÉON (fond sombre, halo lumineux)
   ============================================ */
function NeonDesign({ d }) {
  const c1 = d.accent
  const c2 = '#a855f7'
  return (
    <div className="w-full max-w-xs flex flex-col items-center" style={{ color: '#ffffff' }}>
      <div className="flex items-center gap-3 mb-5">
        <Avatar src={d.logo} size={40} />
        <h1 className="text-lg font-semibold">{d.title}</h1>
      </div>

      {d.rating && (
        <div
          style={{
            fontSize: d.preview ? 64 : 80,
            fontWeight: 800,
            lineHeight: 1,
            letterSpacing: '-0.04em',
            textShadow: `0 0 28px ${c1}`,
          }}
        >
          {d.rating}
        </div>
      )}

      <div className="mt-3">
        <Stars value={d.rating || '5'} size={d.preview ? 24 : 28} glow />
      </div>
      {d.reviewCount && <p className="text-xs mt-2 opacity-70">{d.reviewCount} avis</p>}

      {d.showQr && (
        <div
          className="mt-6"
          style={{
            padding: 3,
            borderRadius: 22,
            background: `linear-gradient(135deg, ${c1}, ${c2})`,
            boxShadow: `0 0 40px ${c1}66`,
          }}
        >
          <div className="bg-white" style={{ borderRadius: 19, padding: 12 }}>
            <QR value={d.qrValue} size={d.preview ? 130 : 160} />
          </div>
        </div>
      )}

      <Tap
        href={d.googleUrl}
        preview={d.preview}
        className="block w-full mt-6 py-3.5 rounded-full text-sm font-semibold text-center"
        style={{
          background: `linear-gradient(135deg, ${c1}, ${c2})`,
          color: '#0b0a1a',
          boxShadow: `0 0 28px ${c1}77`,
        }}
      >
        {d.ctaLabel}
      </Tap>
    </div>
  )
}

/* ============================================
   3. TICKET (ticket de caisse perforé)
   ============================================ */
const BARCODE = [2, 1, 3, 1, 1, 2, 4, 1, 2, 1, 3, 2, 1, 1, 3, 1, 2, 4, 1, 2, 1, 1, 3, 2, 1, 2, 1, 3]

function TicketDesign({ d }) {
  const w = d.preview ? 250 : 300
  const paper = '#fffdf6'
  const ink = '#1c1917'
  const notch = 10
  const maskTop = `radial-gradient(circle at 0 100%, #0000 ${notch}px, #000 ${notch + 0.5}px) left / 51% 100% no-repeat, radial-gradient(circle at 100% 100%, #0000 ${notch}px, #000 ${notch + 0.5}px) right / 51% 100% no-repeat`
  const maskBottom = `radial-gradient(circle at 0 0, #0000 ${notch}px, #000 ${notch + 0.5}px) left / 51% 100% no-repeat, radial-gradient(circle at 100% 0, #0000 ${notch}px, #000 ${notch + 0.5}px) right / 51% 100% no-repeat`

  return (
    <Tap
      href={d.googleUrl}
      preview={d.preview}
      className="block text-left"
      style={{ width: w, fontFamily: MONO, color: ink, filter: 'drop-shadow(0 16px 22px rgba(0,0,0,.28))' }}
    >
      {/* Partie haute */}
      <div
        style={{
          background: paper,
          borderRadius: '16px 16px 0 0',
          padding: '24px 22px 22px',
          WebkitMask: maskTop,
          mask: maskTop,
        }}
      >
        <div className="flex items-center gap-2.5">
          <Avatar src={d.logo} size={32} radius="6px" />
          <div className="font-bold text-sm leading-tight">{d.title}</div>
        </div>
        <div className="mt-4 text-xs opacity-70">Merci de votre visite.</div>
        <div className="mt-1 text-xs opacity-70">Votre avis nous aide à progresser.</div>

        <div className="mt-4 flex items-center gap-2">
          <Stars value={d.rating || '5'} size={20} color={ink} />
          {d.rating && <span className="text-sm font-bold">{d.rating}/5</span>}
        </div>
        {d.reviewCount && <div className="mt-1 text-xs opacity-70">{d.reviewCount} avis clients</div>}
      </div>

      {/* Partie basse */}
      <div
        style={{
          background: paper,
          borderRadius: '0 0 16px 16px',
          padding: '0 22px 22px',
          WebkitMask: maskBottom,
          mask: maskBottom,
        }}
      >
        <div style={{ borderTop: '2px dashed #cfc8b4', margin: '0 8px 20px' }} />

        <div className="flex flex-col items-center text-center">
          {d.showQr && <QR value={d.qrValue} size={Math.round(w * 0.5)} fg={d.accent} bg={paper} />}
          <div className="mt-3 text-xs font-bold">{d.ctaLabel}</div>
          <div className="mt-0.5 text-[11px] opacity-70">Scannez ou touchez ce ticket</div>

          <div className="mt-4 flex items-stretch justify-center" style={{ height: 34, gap: 2 }}>
            {BARCODE.map((n, i) => (
              <span key={i} style={{ width: n, background: d.accent }} />
            ))}
          </div>
        </div>
      </div>
    </Tap>
  )
}

/* ============================================
   4. AFFICHE (typographie, aplat de couleur)
   ============================================ */
function PosterDesign({ d }) {
  const fg = readableText(d.accent)
  return (
    <div className="w-full max-w-sm flex flex-col text-left" style={{ color: fg }}>
      <div className="flex items-center gap-3">
        <Avatar src={d.logo} size={40} />
        <h1 className="text-base font-semibold leading-tight">{d.title}</h1>
      </div>

      <div
        style={{
          fontSize: d.preview ? 92 : 128,
          fontWeight: 900,
          lineHeight: 0.88,
          letterSpacing: '-0.06em',
          marginTop: 32,
        }}
      >
        {d.rating || '5'}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <Stars value={d.rating || '5'} size={d.preview ? 20 : 24} color={fg} stroke={fg} />
        {d.reviewCount && <span className="text-sm font-medium">{d.reviewCount} avis</span>}
      </div>

      <p className="mt-6 font-semibold leading-snug" style={{ fontSize: d.preview ? 17 : 20 }}>
        Votre avis compte.<br />Deux minutes suffisent.
      </p>

      <div className="mt-6 flex items-stretch gap-3">
        <Tap
          href={d.googleUrl}
          preview={d.preview}
          className="flex-1 flex items-center justify-center rounded-full text-sm font-bold text-center"
          style={{ background: fg, color: d.accent, padding: '16px 20px' }}
        >
          {d.ctaLabel}
        </Tap>
        {d.showQr && (
          <div className="bg-white shrink-0" style={{ padding: 8, borderRadius: 16 }}>
            <QR value={d.qrValue} size={64} />
          </div>
        )}
      </div>
    </div>
  )
}

/* ============================================
   5. CONVERSATION (bulles de discussion)
   ============================================ */
function BubbleDesign({ d }) {
  const onAccent = readableText(d.accent)
  const bubble = {
    background: '#ffffff',
    color: '#0f172a',
    boxShadow: '0 6px 16px -6px rgba(15,23,42,.25)',
  }
  return (
    <div className="w-full max-w-xs flex flex-col gap-2.5 text-left">
      <div className="flex items-center gap-3 mb-2">
        <Avatar src={d.logo} size={44} />
        <div>
          <h1 className="text-base font-bold leading-tight">{d.title}</h1>
          {d.rating && (
            <div className="text-xs opacity-70 mt-0.5">
              {d.rating} / 5{d.reviewCount ? ` · ${d.reviewCount} avis` : ''}
            </div>
          )}
        </div>
      </div>

      <div className="self-start px-4 py-2.5 text-sm" style={{ ...bubble, borderRadius: '18px 18px 18px 4px', maxWidth: '85%' }}>
        Merci d’être passé chez {d.title || 'nous'}.
      </div>

      <div className="self-start px-4 py-3 text-sm" style={{ ...bubble, borderRadius: '18px 18px 18px 4px', maxWidth: '85%' }}>
        <div className="mb-2">Comment était votre expérience ?</div>
        <Stars value="5" size={26} />
      </div>

      <Tap
        href={d.googleUrl}
        preview={d.preview}
        className="self-end px-4 py-3 text-sm font-semibold text-center"
        style={{
          background: d.accent,
          color: onAccent,
          borderRadius: '18px 18px 4px 18px',
          maxWidth: '85%',
          boxShadow: `0 8px 18px -8px ${d.accent}`,
        }}
      >
        {d.ctaLabel}
      </Tap>

      {d.showQr && (
        <div className="self-start p-3" style={{ ...bubble, borderRadius: '18px 18px 18px 4px' }}>
          <QR value={d.qrValue} size={d.preview ? 96 : 116} />
          <div className="text-[11px] opacity-60 mt-1.5 text-center">Ou scannez ce code</div>
        </div>
      )}
    </div>
  )
}

const DESIGN_COMPONENTS = {
  simple: SimpleDesign,
  card: CardDesign,
  neon: NeonDesign,
  ticket: TicketDesign,
  poster: PosterDesign,
  bubble: BubbleDesign,
}

/* ============================================
   RENDERER
   ============================================ */
export default function GoogleReviewRenderer({ page, preview = false }) {
  if (!page) return null

  const settings = normalizeSettings(page.settings)
  const design = getReviewDesign(settings.design)
  const theme = getTheme(settings)
  const font = FONTS.find((f) => f.id === settings.font) || FONTS[0]
  const accent = settings.accentColor || design.accent || '#111827'

  const d = {
    preview,
    title: page.title || '',
    logo: page.avatar || page.logo || '',
    rating: settings.rating ? String(settings.rating) : '',
    reviewCount: settings.reviewCount ? String(settings.reviewCount) : '',
    googleUrl: settings.googleUrl || '#',
    qrValue:
      settings.googleUrl ||
      (typeof window !== 'undefined' ? window.location.href : 'https://tapio.link'),
    ctaLabel: settings.ctaLabel || 'Laisser un avis Google',
    tapText: settings.tapText || DEFAULT_TAP_TEXT,
    showQr: settings.showQr !== false,
    accent,
    buttonStyle: getButtonStyle(theme, settings.buttonStyle),
  }

  // Fond et couleur du texte selon le modèle
  let background = getBgStyle(theme)
  let color = theme.text
  let fontFamily = font.family

  if (design.id === 'neon') {
    background = {
      background: `radial-gradient(circle at 50% -10%, ${accent}40, transparent 55%), #0d0b1f`,
    }
    color = '#ffffff'
  } else if (design.id === 'poster') {
    background = { background: accent }
    color = readableText(accent)
  }

  const Design = DESIGN_COMPONENTS[design.id] || SimpleDesign

  return (
    <div
      data-renderer={`GoogleReviewRenderer:${design.id}`}
      className={`${preview ? 'min-h-full py-8' : 'min-h-screen py-10'} flex flex-col items-center justify-center px-6 text-center`}
      style={{ ...background, color, fontFamily }}
    >
      <Design d={d} />
      {!preview && <div className="opacity-40 text-[11px] mt-10">Propulsé par Tapio</div>}
    </div>
  )
}